// src/lib/festival-criteria.js — niveau-onderscheidende criteria per
// functiefamilie (Functiehandboek Filmfestivals 2.0, dec 2025).
//
// Twee soorten criteria:
//  - "kwantitatief"  → Hoofd- en Directie-families: 6-8 criteria, gebruiker
//                       scoort niveau 1..max (3 voor Hoofden, 4 voor Directie).
//                       Score-logica: cumulatief van zwaarste naar lichtste niveau,
//                       eerste niveau dat ≥ `norm.vereist` cumulatieve scores haalt
//                       bepaalt de variant via `variantBijNiveau`. Directie-families
//                       hebben een `fallbackVariant` (laagste schaal) wanneer de
//                       norm niet gehaald wordt — handboek toont dit als lege A-rij.
//  - "kwalitatief"   → Medewerker / Coördinator / Programmeur: 4-5 resultaat-
//                       gebieden, gebruiker kiest A of B per gebied. Variant = letter
//                       waarop ≥ `norm.vereist` gescoord is (B prevaleert).
//
// Bron: bijlage "Uitleg Toepassing Criteria/Niveaus Functies Directeur/Hoofden"
// (p. 55-56). Voorbeeld uit handboek: 2× niveau 2 + 2× niveau 3 → variant op
// niveau 3; 2× niveau 1 + 2× niveau 2 → variant op niveau 2.

export const FAMILIE_CRITERIA = {
  // ============================================================
  // KWANTITATIEF — HOOFD-families (A/B/C, schaal 7-9)
  // ============================================================
  "Hoofd Productie": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 7 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden onder leiding",
        toelichting: "Direct + indirect, intern én extern. Bij meerdere: zwaarste score telt.",
        keuzes: [
          { niveau: 3, label: "Intern 1-5 (of extern 1-15)" },
          { niveau: 2, label: "Intern 6-15 (of extern 16-24)" },
          { niveau: 1, label: "Intern ≥16 (of extern ≥25)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival worden verricht.",
        keuzes: [
          { niveau: 3, label: "1–5 dagen" },
          { niveau: 2, label: "6–10 dagen" },
          { niveau: 1, label: "Meer dan 11 dagen" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        toelichting: "Locaties/zalen waar het festivalprogramma plaatsvindt.",
        keuzes: [
          { niveau: 3, label: "1–5 locaties" },
          { niveau: 2, label: "6–15 locaties" },
          { niveau: 1, label: "Meer dan 16 locaties" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet en/of afdelingsbudget",
        toelichting: "Beheer afdelingsbudget prevaleert boven jaaromzet bij dubbele score.",
        keuzes: [
          { niveau: 3, label: "Klein: jaaromzet ≤ €1M (of afd.budget ≤ €100k)" },
          { niveau: 2, label: "Middel: jaaromzet €1–5M (of afd.budget €100k–€1M)" },
          { niveau: 1, label: "Groot: jaaromzet > €5M (of afd.budget > €1M)" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal geregistreerde bezoeken",
        toelichting: "Totaal aantal fysieke bezoeken aan films en activiteiten (jaarverslag).",
        keuzes: [
          { niveau: 3, label: "< 50.000" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 1, label: "> 150.001" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        toelichting: "Aard van besluitvorming en communicatie bij operationele uitvoering.",
        keuzes: [
          { niveau: 3, label: "Informeren en beïnvloeden van de omgeving" },
          { niveau: 2, label: "Afdelings-overstijgende besluiten nemen en communiceren" },
          { niveau: 1, label: "Project- of festivalbrede besluiten nemen en communiceren" },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        toelichting: "Bijdrage aan beleid/resultaten van de gehele organisatie.",
        keuzes: [
          { niveau: 3, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling" },
          { niveau: 2, label: "Mede-bepalend bij eigen afdeling + bijdrage aan integraal beleid" },
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes / lid MT" },
        ],
      },
    ],
  },

  "Hoofd Programma": {
    type: "kwantitatief",
    norm: { vereist: 5, totaal: 8 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden onder leiding",
        toelichting: "Direct + indirect, intern én extern. Zwaarste score telt.",
        keuzes: [
          { niveau: 3, label: "Intern 1-5 (of extern 1-20)" },
          { niveau: 2, label: "Intern 6-15 (of extern 21-40)" },
          { niveau: 1, label: "Intern ≥16 (of extern >41)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        keuzes: [
          { niveau: 3, label: "1–5 dagen" },
          { niveau: 2, label: "6–10 dagen" },
          { niveau: 1, label: "Meer dan 11 dagen" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        keuzes: [
          { niveau: 3, label: "1–5 locaties" },
          { niveau: 2, label: "6–15 locaties" },
          { niveau: 1, label: "Meer dan 16 locaties" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet en/of afdelingsbudget",
        toelichting: "Afdelingsbudget prevaleert boven jaaromzet.",
        keuzes: [
          { niveau: 3, label: "Klein: jaaromzet ≤ €1M (of afd.budget ≤ €100k)" },
          { niveau: 2, label: "Middel: jaaromzet €1–5M (of afd.budget €100k–€1M)" },
          { niveau: 1, label: "Groot: jaaromzet > €5M (of afd.budget > €1M)" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal geregistreerde bezoeken",
        toelichting: "Alleen voor Hoofd Programma — voor Educatie/Talent/Industry/Nieuwe Media negeren.",
        keuzes: [
          { niveau: 3, label: "< 50.000" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 1, label: "> 150.001" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        toelichting: "Intern + extern; zwaarste score telt.",
        keuzes: [
          { niveau: 3, label: "Informeren en beïnvloeden van de omgeving" },
          { niveau: 2, label: "Afdelings-overstijgende besluiten nemen en communiceren" },
          { niveau: 1, label: "Project- of festivalbrede besluiten nemen en communiceren" },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        keuzes: [
          { niveau: 3, label: "Betrokken bij beleidsontwikkeling eigen team/afdeling" },
          { niveau: 2, label: "Betrokken bij eigen afdeling + bijdrage aan integraal beleid" },
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes / lid MT" },
        ],
      },
      {
        id: "functiebreedte",
        label: "Functiebreedte (aantal deelsectoren)",
        toelichting: "Programma, Educatie, Talent, Industry, Nieuwe Media.",
        keuzes: [
          { niveau: 3, label: "Minder dan drie deelsectoren" },
          { niveau: 2, label: "Drie deelsectoren" },
          { niveau: 1, label: "Vier deelsectoren" },
        ],
      },
    ],
  },

  "Hoofd Development": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 6 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden onder leiding",
        toelichting: "Direct + indirect, intern én extern.",
        keuzes: [
          { niveau: 3, label: "Intern 1-3 (of extern 1-6)" },
          { niveau: 2, label: "Intern 4-6 (of extern 7-14)" },
          { niveau: 1, label: "Intern ≥7 (of extern ≥15)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        keuzes: [
          { niveau: 3, label: "1–5 dagen" },
          { niveau: 2, label: "6–10 dagen" },
          { niveau: 1, label: "Meer dan 11 dagen" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        keuzes: [
          { niveau: 3, label: "1–5 locaties" },
          { niveau: 2, label: "6–15 locaties" },
          { niveau: 1, label: "Meer dan 16 locaties" },
        ],
      },
      {
        id: "omzet",
        label: "Jaaromzet",
        keuzes: [
          { niveau: 3, label: "Klein: ≤ €1M" },
          { niveau: 2, label: "Middel: €1–5M" },
          { niveau: 1, label: "Groot: > €5M" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { niveau: 3, label: "Informeren en beïnvloeden van de omgeving" },
          { niveau: 2, label: "Afdelings-overstijgende besluiten nemen en communiceren" },
          { niveau: 1, label: "Project- of festivalbrede besluiten nemen en communiceren" },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        keuzes: [
          { niveau: 3, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling" },
          { niveau: 2, label: "Mede-bepalend bij eigen afdeling + bijdrage aan integraal beleid" },
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes / lid MT" },
        ],
      },
    ],
  },

  "Hoofd Marketing & Communicatie": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 6 },
    variantBijNiveau: { 1: "C", 2: "B", 3: "A" },
    schaalBijVariant: { A: 7, B: 8, C: 9 },
    criteria: [
      {
        id: "werkenden",
        label: "Aantal werkenden onder leiding",
        toelichting: "Direct + indirect, intern én extern.",
        keuzes: [
          { niveau: 3, label: "Intern 1-3 (of extern 1-5)" },
          { niveau: 2, label: "Intern 4-8 (of extern 6-15)" },
          { niveau: 1, label: "Intern ≥9 (of extern >16)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        keuzes: [
          { niveau: 3, label: "1–5 dagen" },
          { niveau: 2, label: "6–10 dagen" },
          { niveau: 1, label: "Meer dan 11 dagen" },
        ],
      },
      {
        id: "locaties",
        label: "Aantal locaties (en/of zalen)",
        keuzes: [
          { niveau: 3, label: "1–5 locaties" },
          { niveau: 2, label: "6–15 locaties" },
          { niveau: 1, label: "Meer dan 16 locaties" },
        ],
      },
      {
        id: "omzet",
        label: "Omzet en/of afdelingsbudget",
        toelichting: "Afdelingsbudget prevaleert (exclusief barters).",
        keuzes: [
          { niveau: 3, label: "Klein: jaaromzet ≤ €1M (of afd.budget ≤ €100k)" },
          { niveau: 2, label: "Middel: jaaromzet €1–5M (of afd.budget €100k–€500k)" },
          { niveau: 1, label: "Groot: jaaromzet > €5M (of afd.budget > €500k)" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { niveau: 3, label: "Informeren en beïnvloeden van de omgeving" },
          { niveau: 2, label: "Afdelings-overstijgende besluiten nemen en communiceren" },
          { niveau: 1, label: "Project- of festivalbrede besluiten nemen en communiceren" },
        ],
      },
      {
        id: "beslissingsniveau",
        label: "Beslissingsniveau",
        keuzes: [
          { niveau: 3, label: "Mede-bepalend bij beleidsontwikkeling eigen team/afdeling" },
          { niveau: 2, label: "Mede-bepalend bij eigen afdeling + bijdrage aan integraal beleid" },
          { niveau: 1, label: "Mede-beslissend bij integrale beleidskeuzes / lid MT" },
        ],
      },
    ],
  },

  // ============================================================
  // KWALITATIEF — MEDEWERKER- en COÖRDINATOR-families (A/B, schaal 5-7)
  // ============================================================
  "Medewerker Productie": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Vertaalt productiedoelstellingen naar bekende, reguliere projecten/activiteiten" },
          { variant: "B", label: "Vertaalt productiedoelstellingen naar bekende én nieuwe projecten/activiteiten" },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau" },
          { variant: "B", label: "MBO 4 + ervaring met meer samengestelde producties" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: bekende vraagstukken, afweging voor/nadeel, onder begeleiding" },
          { variant: "B", label: "Moeilijk: ook out-of-the-box / alternatieve oplossingen formuleren" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: alleen eigen afdeling · Extern: diverse partijen voor afstemmen" },
          { variant: "B", label: "Intern: ook andere afdelingen + freelancers · Extern: idem" },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Functioneel leiding aan vrijwilligers" },
          { variant: "B", label: "Aanwijzingen aan 1-3 freelancers + functioneel leiding aan vrijwilligers (incl. beoordelen)" },
        ],
      },
    ],
  },

  "Medewerker Programma": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Eén of beperkt aantal projecten/activiteiten" },
          { variant: "B", label: "Meerdere projecten/activiteiten" },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau" },
          { variant: "B", label: "MBO 4 + ervaring" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: bekende vraagstukken, afweging voor/nadeel, onder begeleiding" },
          { variant: "B", label: "Moeilijk: ook out-of-the-box / alternatieve oplossingen formuleren" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: eigen afdeling + vrijwilligers · Extern: diverse partijen voor afstemmen" },
          { variant: "B", label: "Intern: ook andere afdelingen + freelancers voor evaluatie · Extern: idem" },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Geen aanvullend leidinggeven" },
          { variant: "B", label: "Aanwijzingen aan 1-3 freelancers (incl. beoordelen)" },
        ],
      },
    ],
  },

  "Medewerker Development": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Aantal reguliere projecten/activiteiten" },
          { variant: "B", label: "Samengestelde en ingewikkelde programma's naar projecten/activiteiten" },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 werk- en denkniveau" },
          { variant: "B", label: "MBO 4 + ervaring" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: bekende vraagstukken, afweging voor/nadeel, onder begeleiding" },
          { variant: "B", label: "Moeilijk tot erg moeilijk: ook out-of-the-box / alternatieve oplossingen" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: eigen afdeling · Extern: enkele bekende partijen" },
          { variant: "B", label: "Intern: ook andere afdelingen + medewerkers/freelancers · Extern: diverse partijen" },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Geen aanvullend leidinggeven" },
          { variant: "B", label: "Aanwijzingen aan 1-3 medewerkers/freelancers + functioneel leiding aan vrijwilligers" },
        ],
      },
    ],
  },

  "Medewerker Marketing & Communicatie": {
    type: "kwalitatief",
    norm: { vereist: 3, totaal: 4 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Input bij ontwikkeling marketing-/communicatieplan, draagt zorg voor uitvoering" },
          { variant: "B", label: "Coördineert online marketing-werkprocessen (SEO/SEA/sociaal/e-mail), stuurt op output, stuurt redacteuren/externen aan" },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 + min. 2 jaar relevante ervaring" },
          { variant: "B", label: "MBO 4 / HBO + min. 2 jaar relevante ervaring" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: vraagstukken beperken zich tot eigen afdeling" },
          { variant: "B", label: "Moeilijk tot erg moeilijk: ook consequenties voor andere afdelingen afwegen" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: eigen afdeling + informatieve afstemming · Extern: leveranciers" },
          { variant: "B", label: "Intern: adviseren/overtuigen, andere afdelingen, vrijwilligers · Extern: ook overtuigen" },
        ],
      },
    ],
  },

  "Coördinator Programma": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Coördineert de uitvoering van een subonderdeel + voert deels uit" },
          { variant: "B", label: "Levert input voor ontwikkeling + monitort + coördineert uitvoering + stelt jaarplanning op" },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "MBO 4 / HBO werk- en denkniveau" },
          { variant: "B", label: "HBO werk- en denkniveau" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: consequenties voor eigen afdeling afwegen" },
          { variant: "B", label: "Erg moeilijk: consequenties voor andere afdelingen afwegen" },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Beperkte leidinggevende taak (onder verantwoordelijkheid leidinggevende)" },
          { variant: "B", label: "Beperkte leidinggevende taak (onder verantwoordelijkheid leidinggevende)" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: eigen + andere afdelingen voor afstemmen · Extern: beperkt aantal partijen" },
          { variant: "B", label: "Intern: eigen afdeling besluitvormend + andere afdelingen afstemmen · Extern: diverse partijen" },
        ],
      },
    ],
  },

  "Programmeur": {
    type: "kwalitatief",
    norm: { vereist: 4, totaal: 5 },
    criteria: [
      {
        id: "reikwijdte",
        label: "Reikwijdte (invloed & gevolg)",
        keuzes: [
          { variant: "A", label: "Stelt programma's samen binnen missie/visie/strategie van artistiek directeur; assisteert bij uitvoering programmabeleid" },
          { variant: "B", label: "Levert input voor organisatiebrede missie/visie; ontwikkelt en implementeert selectiecriteria en programmabeleid" },
        ],
      },
      {
        id: "kennis",
        label: "Kennis",
        keuzes: [
          { variant: "A", label: "HBO werk- en denkniveau" },
          { variant: "B", label: "HBO+ / WO werk- en denkniveau" },
        ],
      },
      {
        id: "complexiteit",
        label: "Complexiteit",
        keuzes: [
          { variant: "A", label: "Moeilijk: uitvoerende vraagstukken; geen strategische verantwoordelijkheid; adviseert maar beslist niet" },
          { variant: "B", label: "Erg moeilijk: complexe vraagstukken met consequenties voor meerdere afdelingen; besluit over programmering" },
        ],
      },
      {
        id: "leidinggeven",
        label: "Leidinggeven",
        keuzes: [
          { variant: "A", label: "Aanwijzingen aan freelancers + externe viewers/selectiecommissies; kan stagiairs begeleiden" },
          { variant: "B", label: "Idem + fungeert als mentor voor Programmeur A" },
        ],
      },
      {
        id: "communicatie",
        label: "Communicatie",
        keuzes: [
          { variant: "A", label: "Intern: ondersteunend en adviserend · Extern: kleinere samenwerkingen" },
          { variant: "B", label: "Intern: sturend, adviserend, overtuigend · Extern: onderhandelt over inhoudelijke samenwerkingen" },
        ],
      },
    ],
  },

  // ============================================================
  // KWANTITATIEF — DIRECTIE-families (A/B/C/D/E, schaal 8-12)
  // ============================================================
  // Score-logica: niveau 1 (zwaarste) → E (12), 2 → D (11), 3 → C (10),
  // 4 → B (9). Norm niet gehaald → fallback A (8) — de lege rij in het
  // handboek. Criteria-data uit p. 49 (Algemeen), p. 51 (Zakelijk),
  // p. 53 (Artistiek).

  "Algemeen Directeur": {
    type: "kwantitatief",
    norm: { vereist: 5, totaal: 8 },
    variantBijNiveau: { 1: "E", 2: "D", 3: "C", 4: "B" },
    fallbackVariant: "A",
    schaalBijVariant: { A: 8, B: 9, C: 10, D: 11, E: 12 },
    criteria: [
      {
        id: "inhoud_programmering",
        label: "Inhoud programmering",
        toelichting: "Samenstelling en planning van het volledige aanbod en de premières.",
        keuzes: [
          { niveau: 1, label: "> 200 films · overwegend wereldpremières én Europese premières" },
          { niveau: 2, label: "> 200 films · overwegend Europese premières en/of nationale premières" },
          { niveau: 3, label: "< 200 films · overwegend wereld-/Europese/nationale premières" },
          { niveau: 4, label: "< 200 films · minder dan helft premières" },
        ],
      },
      {
        id: "deelsectoren",
        label: "Functiebreedte / aantal deelsectoren",
        toelichting: "Eindverantwoordelijk voor X deelsectoren (Filmprogramma, Educatie, Talent, Industry, Nieuwe Media).",
        keuzes: [
          { niveau: 1, label: "5 deelsectoren" },
          { niveau: 2, label: "4 deelsectoren" },
          { niveau: 3, label: "3 deelsectoren" },
          { niveau: 4, label: "1 of 2 deelsectoren" },
        ],
      },
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Intern (hiërarchisch direct + indirect) of extern (aanwijzingen aan zzp-ers); zwaarste score telt.",
        keuzes: [
          { niveau: 1, label: "Intern ≥ 50 (of extern ≥ 100)" },
          { niveau: 2, label: "Intern 25-49 (of extern 50-99)" },
          { niveau: 3, label: "Intern 5-24 (of extern 25-49)" },
          { niveau: 4, label: "Intern 1-4 (of extern 5-24)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        toelichting: "Het aantal dagen dat werkzaamheden op het filmfestival worden verricht.",
        keuzes: [
          { niveau: 1, label: "> 11 dagen" },
          { niveau: 2, label: "6 – 10 dagen" },
          { niveau: 3, label: "4 – 5 dagen" },
          { niveau: 4, label: "1 – 3 dagen" },
        ],
      },
      {
        id: "budget",
        label: "Budgetverantwoordelijkheid",
        toelichting: "Totale gerealiseerde jaaromzet zoals gerapporteerd in jaarrekening.",
        keuzes: [
          { niveau: 1, label: "Groot: > € 5.000.000" },
          { niveau: 2, label: "Middelgroot: € 1.000.001 – € 5.000.000" },
          { niveau: 3, label: "Klein: € 500.001 – € 1.000.000" },
          { niveau: 4, label: "Kleiner: ≤ € 500.000" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        toelichting: "Totaal geregistreerde fysieke bezoeken aan films en festivalactiviteiten (jaarverslag).",
        keuzes: [
          { niveau: 1, label: "> 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "25.001 – 50.000" },
          { niveau: 4, label: "< 25.000" },
        ],
      },
      {
        id: "betekenis",
        label: "Maatschappelijke en culturele betekenis",
        toelichting: "Uitstraling op cultureel/maatschappelijk vlak en vertegenwoordigingsbereik.",
        keuzes: [
          { niveau: 1, label: "Internationaal én nationaal · internationale vertegenwoordiging" },
          { niveau: 2, label: "Nationaal, regionaal én lokaal · nationale vertegenwoordiging" },
          { niveau: 3, label: "Provinciaal/regionaal · regionale vertegenwoordiging" },
          { niveau: 4, label: "Overwegend stedelijk · lokale vertegenwoordiging" },
        ],
      },
      {
        id: "stakeholders",
        label: "Complexiteit Stakeholdersveld",
        toelichting: "Aantal partijen buiten de eigen organisatie waarmee noodzakelijke afstemming plaatsvindt.",
        keuzes: [
          { niveau: 1, label: "> 100 partijen" },
          { niveau: 2, label: "75 – 100 partijen" },
          { niveau: 3, label: "50 – 75 partijen" },
          { niveau: 4, label: "< 50 partijen" },
        ],
      },
    ],
  },

  "Zakelijk Directeur": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 7 },
    variantBijNiveau: { 1: "E", 2: "D", 3: "C", 4: "B" },
    fallbackVariant: "A",
    schaalBijVariant: { A: 8, B: 9, C: 10, D: 11, E: 12 },
    criteria: [
      {
        id: "deelsectoren",
        label: "Functiebreedte / aantal deelsectoren",
        keuzes: [
          { niveau: 1, label: "5 deelsectoren" },
          { niveau: 2, label: "4 deelsectoren" },
          { niveau: 3, label: "3 deelsectoren" },
          { niveau: 4, label: "1 of 2 deelsectoren" },
        ],
      },
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Intern of extern; zwaarste score telt.",
        keuzes: [
          { niveau: 1, label: "Intern ≥ 50 (of extern ≥ 100)" },
          { niveau: 2, label: "Intern 25-49 (of extern 50-99)" },
          { niveau: 3, label: "Intern 5-24 (of extern 25-49)" },
          { niveau: 4, label: "Intern 1-4 (of extern 5-24)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        keuzes: [
          { niveau: 1, label: "> 11 dagen" },
          { niveau: 2, label: "6 – 10 dagen" },
          { niveau: 3, label: "4 – 5 dagen" },
          { niveau: 4, label: "1 – 3 dagen" },
        ],
      },
      {
        id: "budget",
        label: "Budgetverantwoordelijkheid",
        toelichting: "Totale gerealiseerde jaaromzet.",
        keuzes: [
          { niveau: 1, label: "Groot: > € 5.000.000" },
          { niveau: 2, label: "Middelgroot: € 1.000.001 – € 5.000.000" },
          { niveau: 3, label: "Klein: € 500.001 – € 1.000.000" },
          { niveau: 4, label: "Kleiner: ≤ € 500.000" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        keuzes: [
          { niveau: 1, label: "> 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "25.001 – 50.000" },
          { niveau: 4, label: "< 25.000" },
        ],
      },
      {
        id: "betekenis",
        label: "Maatschappelijke en culturele betekenis",
        keuzes: [
          { niveau: 1, label: "Internationaal én nationaal · internationale vertegenwoordiging" },
          { niveau: 2, label: "Nationaal, regionaal én lokaal · nationale vertegenwoordiging" },
          { niveau: 3, label: "Provinciaal/regionaal · regionale vertegenwoordiging" },
          { niveau: 4, label: "Overwegend stedelijk · lokale vertegenwoordiging" },
        ],
      },
      {
        id: "stakeholders",
        label: "Complexiteit Stakeholdersveld",
        keuzes: [
          { niveau: 1, label: "> 100 partijen" },
          { niveau: 2, label: "75 – 100 partijen" },
          { niveau: 3, label: "50 – 75 partijen" },
          { niveau: 4, label: "< 50 partijen" },
        ],
      },
    ],
  },

  "Artistiek Directeur": {
    type: "kwantitatief",
    norm: { vereist: 4, totaal: 7 },
    variantBijNiveau: { 1: "E", 2: "D", 3: "C", 4: "B" },
    fallbackVariant: "A",
    schaalBijVariant: { A: 8, B: 9, C: 10, D: 11, E: 12 },
    criteria: [
      {
        id: "inhoud_programmering",
        label: "Inhoud programmering",
        toelichting: "Samenstelling en planning van het volledige aanbod en de premières.",
        keuzes: [
          { niveau: 1, label: "> 200 films · overwegend wereldpremières én Europese premières" },
          { niveau: 2, label: "> 200 films · overwegend Europese premières en/of nationale premières" },
          { niveau: 3, label: "< 200 films · overwegend wereld-/Europese/nationale premières" },
          { niveau: 4, label: "< 200 films · minder dan helft premières" },
        ],
      },
      {
        id: "deelsectoren",
        label: "Functiebreedte / aantal deelsectoren",
        keuzes: [
          { niveau: 1, label: "5 deelsectoren" },
          { niveau: 2, label: "4 deelsectoren" },
          { niveau: 3, label: "3 deelsectoren" },
          { niveau: 4, label: "1 of 2 deelsectoren" },
        ],
      },
      {
        id: "werkenden",
        label: "Aantal werkenden",
        toelichting: "Intern of extern; zwaarste score telt.",
        keuzes: [
          { niveau: 1, label: "Intern ≥ 50 (of extern ≥ 100)" },
          { niveau: 2, label: "Intern 25-49 (of extern 50-99)" },
          { niveau: 3, label: "Intern 5-24 (of extern 25-49)" },
          { niveau: 4, label: "Intern 1-4 (of extern 5-24)" },
        ],
      },
      {
        id: "festivaldagen",
        label: "Aantal filmfestivaldagen",
        keuzes: [
          { niveau: 1, label: "> 11 dagen" },
          { niveau: 2, label: "6 – 10 dagen" },
          { niveau: 3, label: "4 – 5 dagen" },
          { niveau: 4, label: "1 – 3 dagen" },
        ],
      },
      {
        id: "bezoeken",
        label: "Aantal bezoeken",
        keuzes: [
          { niveau: 1, label: "> 150.001" },
          { niveau: 2, label: "50.001 – 150.000" },
          { niveau: 3, label: "25.001 – 50.000" },
          { niveau: 4, label: "< 25.000" },
        ],
      },
      {
        id: "betekenis",
        label: "Maatschappelijke en culturele betekenis",
        keuzes: [
          { niveau: 1, label: "Internationaal én nationaal · internationale vertegenwoordiging" },
          { niveau: 2, label: "Nationaal, regionaal én lokaal · nationale vertegenwoordiging" },
          { niveau: 3, label: "Provinciaal/regionaal · regionale vertegenwoordiging" },
          { niveau: 4, label: "Overwegend stedelijk · lokale vertegenwoordiging" },
        ],
      },
      {
        id: "stakeholders",
        label: "Complexiteit Stakeholdersveld",
        keuzes: [
          { niveau: 1, label: "> 100 partijen" },
          { niveau: 2, label: "75 – 100 partijen" },
          { niveau: 3, label: "50 – 75 partijen" },
          { niveau: 4, label: "< 50 partijen" },
        ],
      },
    ],
  },
};

// ============================================================
// SCORE-ALGORITME
// ============================================================

/**
 * Bepaal de variant (A/B/C of A/B) op basis van de gegeven antwoorden.
 * @param {Object} antwoorden  - { [criteriumId]: keuzeObject | null }
 * @param {string} familieKey  - sleutel in FAMILIE_CRITERIA
 * @returns {{ variant: string|null, gescoord: number, vereist: number, totaal: number, uitleg: string }}
 */
export function bepaalVariant(antwoorden, familieKey) {
  const fam = FAMILIE_CRITERIA[familieKey];
  if (!fam) return null;

  const beantwoord = Object.values(antwoorden || {}).filter(Boolean);
  const totaalBeantwoord = beantwoord.length;

  if (fam.type === "kwantitatief") {
    // Cumulatief van zwaarste niveau (1) naar lichtste (max): eerste niveau N
    // waarop ≥ vereist criteria gescoord zijn op niveau ≤ N bepaalt de variant.
    // Range wordt afgeleid uit variantBijNiveau (3 voor Hoofden, 4 voor Directie).
    const niveaus = beantwoord.map(a => a.niveau).filter(n => Number.isFinite(n));
    const maxNiveau = Math.max(...Object.keys(fam.variantBijNiveau).map(Number));
    for (let n = 1; n <= maxNiveau; n++) {
      const aantalOpOfBoven = niveaus.filter(x => x <= n).length;
      if (aantalOpOfBoven >= fam.norm.vereist) {
        return {
          variant: fam.variantBijNiveau[n],
          gescoord: aantalOpOfBoven,
          vereist: fam.norm.vereist,
          totaal: fam.norm.totaal,
          beantwoord: totaalBeantwoord,
          uitleg: `${aantalOpOfBoven} van ${fam.norm.totaal} criteria voldoen op niveau ${n} of zwaarder → variant ${fam.variantBijNiveau[n]}.`,
        };
      }
    }
    // Norm niet gehaald: families met expliciete fallbackVariant (Directie:
    // lege A-rij in handboek) vallen daarop terug — maar pas zodra ALLE
    // criteria beantwoord zijn (anders verschijnt A al bij 0 antwoorden).
    // Andere families (Hoofden) geven null tot er een variant matcht.
    const alleBeantwoord = totaalBeantwoord >= fam.criteria.length;
    if (fam.fallbackVariant && alleBeantwoord) {
      return {
        variant: fam.fallbackVariant,
        gescoord: niveaus.filter(x => x <= maxNiveau).length,
        vereist: fam.norm.vereist,
        totaal: fam.norm.totaal,
        beantwoord: totaalBeantwoord,
        uitleg: `Norm (${fam.norm.vereist} van ${fam.norm.totaal}) niet gehaald → variant ${fam.fallbackVariant} (laagste schaal).`,
      };
    }
    return {
      variant: null,
      gescoord: niveaus.filter(x => x <= maxNiveau).length,
      vereist: fam.norm.vereist,
      totaal: fam.norm.totaal,
      beantwoord: totaalBeantwoord,
      uitleg: `Norm niet gehaald (${fam.norm.vereist} van ${fam.norm.totaal} criteria vereist). Beantwoord meer vragen.`,
    };
  }

  if (fam.type === "kwalitatief") {
    const aCount = beantwoord.filter(x => x.variant === "A").length;
    const bCount = beantwoord.filter(x => x.variant === "B").length;
    // B-variant is zwaarder; hanteer B prevaleert bij norm-overschrijding
    if (bCount >= fam.norm.vereist) {
      return {
        variant: "B",
        gescoord: bCount,
        vereist: fam.norm.vereist,
        totaal: fam.norm.totaal,
        beantwoord: totaalBeantwoord,
        uitleg: `${bCount} van ${fam.norm.totaal} criteria op niveau B → variant B.`,
      };
    }
    if (aCount >= fam.norm.vereist) {
      return {
        variant: "A",
        gescoord: aCount,
        vereist: fam.norm.vereist,
        totaal: fam.norm.totaal,
        beantwoord: totaalBeantwoord,
        uitleg: `${aCount} van ${fam.norm.totaal} criteria op niveau A → variant A.`,
      };
    }
    return {
      variant: null,
      gescoord: Math.max(aCount, bCount),
      vereist: fam.norm.vereist,
      totaal: fam.norm.totaal,
      beantwoord: totaalBeantwoord,
      aCount,
      bCount,
      uitleg: totaalBeantwoord === fam.criteria.length
        ? `Je antwoorden zijn verdeeld over beide varianten: ${aCount}× A en ${bCount}× B. De norm vereist ${fam.norm.vereist} van ${fam.norm.totaal} op één variant. Herzie een paar antwoorden zodat één variant duidelijk overweegt.`
        : `Norm niet gehaald (${fam.norm.vereist} van ${fam.norm.totaal} criteria vereist op één variant). Beantwoord meer vragen.`,
    };
  }

  return null;
}

export function hasCriteria(familieKey) {
  return Boolean(FAMILIE_CRITERIA[familieKey]);
}
